import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface FABProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible label — the FAB is icon-only visually. */
  label: string;
  /** Render as a Slot so a Link or any other element can wear FAB styling. */
  asChild?: boolean;
  /**
   * Slow shadow-glow pulse to attract attention (e.g., on an empty
   * dashboard). Off by default; turn on only when nothing else on
   * screen is inviting the user in.
   */
  pulse?: boolean;
}

/**
 * Floating action button — coral pill, warm glow shadow.
 * Pass `asChild` to render as any element (e.g. a react-router <Link>).
 */
export const FAB = forwardRef<HTMLButtonElement, FABProps>(
  ({ className, label, children, asChild = false, pulse = false, ...props }, ref) => {
    const Comp: any = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        {...(asChild ? {} : { type: "button" })}
        aria-label={label}
        // In-feed ad banners hide while they'd overlap this (native views
        // paint above the WebView). See InlineAd.
        data-ad-avoid=""
        className={cn(
          // Bottom offset stacks 24 px + the bottom tab bar height (a CSS var
          // that resolves to 0 when the tabs are unmounted) so the FAB never
          // sits under the tabs. The ad slot is inline now, so it no longer
          // competes for bottom space.
          // z-40, not z-30: BottomTabs/TripActionBar are also z-30 and mount
          // AFTER the FAB in the DOM (siblings after <Outlet/>), so at equal
          // z-index they'd paint on top and hide the FAB behind the bar.
          "fixed right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full",
          "bottom-[calc(24px+var(--bottom-tabs-h,0px))]",
          "bg-primary text-white transition-transform duration-200 active:scale-95",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
          pulse && "fab-idle-pulse",
          className,
        )}
        style={{
          boxShadow:
            "0 8px 20px rgba(228,87,46,0.35), 0 2px 6px rgba(0,0,0,0.15)",
        }}
        {...props}
      >
        {children ?? <Plus className="h-6 w-6" strokeWidth={2.5} />}
      </Comp>
    );
  },
);
FAB.displayName = "FAB";
